import { type ReactNode } from 'react';
import { usePortal } from './PortalContext';
import { Loader2, AlertCircle } from 'lucide-react';
import { DEFAULT_DOCUMENT_ACCENT } from '../../lib/brandAccent';

interface PortalLayoutProps {
    children: ReactNode;
}

export const PortalLayout = ({ children }: PortalLayoutProps) => {
    const { org, customer, loading, error, isValid } = usePortal();

    // Loading state
    if (loading) {
        return (
            <div className="min-h-screen bg-canvas flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-success mx-auto mb-4" />
                    <p className="text-fg-3">Loading your portal...</p>
                </div>
            </div>
        );
    }

    // Error / Invalid token
    if (error || !isValid) {
        return (
            <div className="min-h-screen bg-canvas flex items-center justify-center p-4">
                <div className="max-w-md w-full bg-card rounded-2xl border border-line p-8 text-center">
                    <div className="w-16 h-16 bg-danger/10 rounded-full flex items-center justify-center mx-auto mb-6">
                        <AlertCircle className="w-8 h-8 text-danger" />
                    </div>
                    <h1 className="text-xl font-bold text-fg mb-2">Link Expired or Invalid</h1>
                    <p className="text-fg-3 mb-6">
                        {error || 'This link is no longer valid. Please request a new one from your service provider.'}
                    </p>
                    <p className="text-xs text-fg-4">
                        If you believe this is an error, contact the business directly.
                    </p>
                </div>
            </div>
        );
    }

    // Dynamic accent color from org branding
    const accentColor = org?.accent_color || DEFAULT_DOCUMENT_ACCENT;

    return (
        <div
            className="min-h-screen bg-canvas text-fg"
            style={{ '--portal-accent': accentColor } as React.CSSProperties}
        >
            {/* Header */}
            <header className="sticky top-0 z-50 bg-card/80 backdrop-blur-xl border-b border-line">
                <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        {org?.logo_url ? (
                            <img src={org.logo_url} alt={org.name} className="h-8 w-auto" />
                        ) : (
                            <div
                                className="w-10 h-10 rounded-lg flex items-center justify-center font-bold text-on-fill"
                                style={{ backgroundColor: accentColor }}
                            >
                                {org?.name?.[0] || 'P'}
                            </div>
                        )}
                        <div>
                            <h1 className="font-bold text-fg text-sm">{org?.name || 'Customer Portal'}</h1>
                            <p className="text-xs text-fg-3">Customer Portal</p>
                        </div>
                    </div>
                    <div className="text-right">
                        <p className="text-sm font-medium text-fg">{customer?.name}</p>
                        <p className="text-xs text-fg-3">{customer?.email}</p>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="max-w-4xl mx-auto px-4 py-8">
                {children}
            </main>

            {/* Footer */}
            <footer className="border-t border-line mt-auto">
                <div className="max-w-4xl mx-auto px-4 py-6 text-center text-xs text-fg-4">
                    <p>Powered by ForgeDash</p>
                </div>
            </footer>
        </div>
    );
};
